import { useState } from 'react'
import type { Workspace } from '../../context/WorkspaceContext'
import { isModuleEnabled, type ModuleKey } from '../../utils/workspaceModules'
import { MODULE_TITLE_NLS_KEYS } from '../../utils/workspaceModulesSurfaces'
import { isClientOutreachTierEligible } from '../../services/featureRegistry'
import {
  ConfirmActionModal,
  SettingsReadOnlyRow,
  SettingsRow,
  SettingsSection,
  Toggle,
  useInstantSettingRows,
} from '../Common'
import { BudgetEnforcementControl } from './BudgetEnforcementControl'
import { useNls } from '../../nls/nlsProvider'
import type { NlsKey } from '../../nls/strings_nls'

export type ModuleSettingKey =
  | `${ModuleKey}Enabled`
  | 'helperCountEnabled'
  | 'helperFormsEnabled'
  | 'helperScanEnabled'

export interface WorkspaceModulesPanelProps {
  workspace: Workspace
  canEdit: boolean
  onSave: (key: ModuleSettingKey, enabled: boolean) => Promise<void>
  onError: (error: string | null) => void
}

interface AddonCardConfig {
  key: `${ModuleKey}Enabled`
  moduleKey: ModuleKey
  titleKey: NlsKey
  subtitleKey:
    | 'NLSWorkspaceModuleAnalysisSubtitle'
    | 'NLSWorkspaceModuleAutomateSubtitle'
    | 'NLSWorkspaceModuleBudgetSubtitle'
    | 'NLSWorkspaceModuleClientOutreachSubtitle'
    | 'NLSWorkspaceModuleDesignSubtitle'
    | 'NLSWorkspaceModuleFormsSubtitle'
    | 'NLSWorkspaceModuleLinkHubSubtitle'
    | 'NLSWorkspaceModuleMeetingsSubtitle'
    | 'NLSWorkspaceModuleQuotaSubtitle'
    | 'NLSWorkspaceModuleReportsSubtitle'
    | 'NLSWorkspaceModuleStockSubtitle'
    | 'NLSWorkspaceModuleTeachingSubtitle'
    | 'NLSWorkspaceModuleTrainingSubtitle'
    | 'NLSWorkspaceModuleConstructionSubtitle'
    | 'NLSWorkspaceModuleHospitalSubtitle'
    | 'NLSWorkspaceModuleSitesSubtitle'
    | 'NLSWorkspaceModuleShopsSubtitle'
    | 'NLSWorkspaceModuleStaysSubtitle'
    | 'NLSWorkspaceModuleJobsSubtitle'
    | 'NLSWorkspaceModuleClinicsSubtitle'
    | 'NLSWorkspaceModulePlantSubtitle'
    | 'NLSWorkspaceModuleFitnessSubtitle'
    | 'NLSWorkspaceModuleUploadSubtitle'
}

const ADDON_CARDS: readonly AddonCardConfig[] = [
  {
    key: 'analysisEnabled',
    moduleKey: 'analysis',
    titleKey: MODULE_TITLE_NLS_KEYS.analysis,
    subtitleKey: 'NLSWorkspaceModuleAnalysisSubtitle',
  },
  {
    key: 'automateEnabled',
    moduleKey: 'automate',
    titleKey: MODULE_TITLE_NLS_KEYS.automate,
    subtitleKey: 'NLSWorkspaceModuleAutomateSubtitle',
  },
  {
    key: 'budgetEnabled',
    moduleKey: 'budget',
    titleKey: MODULE_TITLE_NLS_KEYS.budget,
    subtitleKey: 'NLSWorkspaceModuleBudgetSubtitle',
  },
  {
    key: 'clientOutreachEnabled',
    moduleKey: 'clientOutreach',
    titleKey: MODULE_TITLE_NLS_KEYS.clientOutreach,
    subtitleKey: 'NLSWorkspaceModuleClientOutreachSubtitle',
  },
  {
    key: 'designEnabled',
    moduleKey: 'design',
    titleKey: MODULE_TITLE_NLS_KEYS.design,
    subtitleKey: 'NLSWorkspaceModuleDesignSubtitle',
  },
  {
    key: 'formsEnabled',
    moduleKey: 'forms',
    titleKey: MODULE_TITLE_NLS_KEYS.forms,
    subtitleKey: 'NLSWorkspaceModuleFormsSubtitle',
  },
  {
    key: 'linkHubEnabled',
    moduleKey: 'linkHub',
    titleKey: MODULE_TITLE_NLS_KEYS.linkHub,
    subtitleKey: 'NLSWorkspaceModuleLinkHubSubtitle',
  },
  {
    key: 'meetingsEnabled',
    moduleKey: 'meetings',
    titleKey: MODULE_TITLE_NLS_KEYS.meetings,
    subtitleKey: 'NLSWorkspaceModuleMeetingsSubtitle',
  },
  {
    key: 'quotaEnabled',
    moduleKey: 'quota',
    titleKey: MODULE_TITLE_NLS_KEYS.quota,
    subtitleKey: 'NLSWorkspaceModuleQuotaSubtitle',
  },
  {
    key: 'reportsEnabled',
    moduleKey: 'reports',
    titleKey: MODULE_TITLE_NLS_KEYS.reports,
    subtitleKey: 'NLSWorkspaceModuleReportsSubtitle',
  },
  {
    key: 'stockEnabled',
    moduleKey: 'stock',
    titleKey: MODULE_TITLE_NLS_KEYS.stock,
    subtitleKey: 'NLSWorkspaceModuleStockSubtitle',
  },
  {
    key: 'teachingEnabled',
    moduleKey: 'teaching',
    titleKey: MODULE_TITLE_NLS_KEYS.teaching,
    subtitleKey: 'NLSWorkspaceModuleTeachingSubtitle',
  },
  {
    key: 'trainingEnabled',
    moduleKey: 'training',
    titleKey: MODULE_TITLE_NLS_KEYS.training,
    subtitleKey: 'NLSWorkspaceModuleTrainingSubtitle',
  },
  {
    key: 'constructionEnabled',
    moduleKey: 'construction',
    titleKey: MODULE_TITLE_NLS_KEYS.construction,
    subtitleKey: 'NLSWorkspaceModuleConstructionSubtitle',
  },
  {
    key: 'hospitalEnabled',
    moduleKey: 'hospital',
    titleKey: MODULE_TITLE_NLS_KEYS.hospital,
    subtitleKey: 'NLSWorkspaceModuleHospitalSubtitle',
  },
  {
    key: 'sitesEnabled',
    moduleKey: 'sites',
    titleKey: MODULE_TITLE_NLS_KEYS.sites,
    subtitleKey: 'NLSWorkspaceModuleSitesSubtitle',
  },
  {
    key: 'shopsEnabled',
    moduleKey: 'shops',
    titleKey: MODULE_TITLE_NLS_KEYS.shops,
    subtitleKey: 'NLSWorkspaceModuleShopsSubtitle',
  },
  {
    key: 'staysEnabled',
    moduleKey: 'stays',
    titleKey: MODULE_TITLE_NLS_KEYS.stays,
    subtitleKey: 'NLSWorkspaceModuleStaysSubtitle',
  },
  {
    key: 'jobsEnabled',
    moduleKey: 'jobs',
    titleKey: MODULE_TITLE_NLS_KEYS.jobs,
    subtitleKey: 'NLSWorkspaceModuleJobsSubtitle',
  },
  {
    key: 'clinicsEnabled',
    moduleKey: 'clinics',
    titleKey: MODULE_TITLE_NLS_KEYS.clinics,
    subtitleKey: 'NLSWorkspaceModuleClinicsSubtitle',
  },
  {
    key: 'plantEnabled',
    moduleKey: 'plant',
    titleKey: MODULE_TITLE_NLS_KEYS.plant,
    subtitleKey: 'NLSWorkspaceModulePlantSubtitle',
  },
  {
    key: 'fitnessEnabled',
    moduleKey: 'fitness',
    titleKey: MODULE_TITLE_NLS_KEYS.fitness,
    subtitleKey: 'NLSWorkspaceModuleFitnessSubtitle',
  },
  {
    key: 'uploadEnabled',
    moduleKey: 'upload',
    titleKey: MODULE_TITLE_NLS_KEYS.upload,
    subtitleKey: 'NLSWorkspaceModuleUploadSubtitle',
  },
] as const

const FUN_PACK_CARD = {
  key: 'funPackEnabled' as const,
  moduleKey: 'funPack' as const,
  titleKey: MODULE_TITLE_NLS_KEYS.funPack,
  subtitleKey: 'NLSWorkspaceModuleFunPackSubtitle' as const,
}

interface HelperCardConfig {
  key: 'helperCountEnabled' | 'helperFormsEnabled' | 'helperScanEnabled'
  titleKey:
    | 'NLSWorkspaceModuleHelperCountTitle'
    | 'NLSWorkspaceModuleHelperFormsTitle'
    | 'NLSWorkspaceModuleHelperScanTitle'
  subtitleKey:
    | 'NLSAdminModuleHelperCountSubtitle'
    | 'NLSAdminModuleHelperFormsSubtitle'
    | 'NLSAdminModuleHelperScanSubtitle'
  requiredModule?: ModuleKey
  disabledReasonKey?:
    | 'NLSWorkspaceModulesEnableStockFirst'
    | 'NLSWorkspaceModulesEnableFormsFirst'
}

const HELPER_CARDS: readonly HelperCardConfig[] = [
  {
    key: 'helperCountEnabled',
    titleKey: 'NLSWorkspaceModuleHelperCountTitle',
    subtitleKey: 'NLSAdminModuleHelperCountSubtitle',
    requiredModule: 'stock',
    disabledReasonKey: 'NLSWorkspaceModulesEnableStockFirst',
  },
  {
    key: 'helperFormsEnabled',
    titleKey: 'NLSWorkspaceModuleHelperFormsTitle',
    subtitleKey: 'NLSAdminModuleHelperFormsSubtitle',
    requiredModule: 'forms',
    disabledReasonKey: 'NLSWorkspaceModulesEnableFormsFirst',
  },
  {
    key: 'helperScanEnabled',
    titleKey: 'NLSWorkspaceModuleHelperScanTitle',
    subtitleKey: 'NLSAdminModuleHelperScanSubtitle',
  },
] as const

const ModuleSettingTitleKeysMap: Record<ModuleSettingKey, NlsKey> = [
  ...ADDON_CARDS,
  FUN_PACK_CARD,
  ...HELPER_CARDS,
].reduce((acc, card) => {
  acc[card.key] = card.titleKey
  return acc
}, {} as Record<ModuleSettingKey, NlsKey>)

export const MODULE_SETTING_TITLE_KEYS: Record<ModuleSettingKey, NlsKey> =
  ModuleSettingTitleKeysMap

export const WorkspaceModulesPanel = ({
  workspace,
  canEdit,
  onSave,
  onError,
}: WorkspaceModulesPanelProps) => {
  const { t } = useNls()
  const instantRows = useInstantSettingRows()
  const [pendingTurnOff, setPendingTurnOff] = useState<{
    key: ModuleSettingKey
    title: string
  } | null>(null)

  const handleToggle = (
    key: ModuleSettingKey,
    title: string,
    currentEnabled: boolean,
    nextChecked: boolean,
  ) => {
    if (!nextChecked && currentEnabled) {
      setPendingTurnOff({ key, title })
    } else if (nextChecked && !currentEnabled) {
      void instantRows.run(key, async () => {
        await onSave(key, true)
      })
    }
  }

  const handleConfirmTurnOff = async () => {
    if (!pendingTurnOff) return
    const { key } = pendingTurnOff
    setPendingTurnOff(null)
    await instantRows.run(key, async () => {
      await onSave(key, false)
    })
  }

  const renderModuleRow = (
    key: ModuleSettingKey,
    title: string,
    subtitle: string,
    enabled: boolean,
    toggleDisabled: boolean,
    tooltip?: string,
  ) => {
    const hint = tooltip || subtitle
    return (
      <div key={key} className="resourceModuleCard">
        <SettingsRow
          label={<span className="railPageShellSectionHeader__title">{title}</span>}
          hint={hint}
          status={instantRows.statusFor(key)}
          control={
            <div title={tooltip}>
              <Toggle
                wrapperClassName="resourceSettingsToggle inviteEditToggle"
                checked={enabled}
                disabled={toggleDisabled}
                onChange={(checked) => handleToggle(key, title, enabled, checked)}
                label={
                  <span>
                    <strong>
                      {enabled
                        ? t('NLSWorkspaceModulesToggleEnabled')
                        : t('NLSWorkspaceModulesToggleDisabled')}
                    </strong>
                    <small>
                      {t('NLSWorkspaceModulesToggleAria', {
                        module: title.toLowerCase(),
                      })}
                    </small>
                  </span>
                }
              />
            </div>
          }
        />
        {key === 'budgetEnabled' && enabled && (
          <BudgetEnforcementControl
            workspace={workspace}
            canEdit={canEdit}
            onError={onError}
          />
        )}
      </div>
    )
  }

  return (
    <div className="workspaceModulesPanel">
      {/* Group 1: Modules (Addons + Fun pack) */}
      <SettingsSection
        title={t('NLSBoardSettingsSectionModules')}
        description={t('NLSBoardSettingsSectionModulesDesc')}
      >
        {!canEdit && (
          <SettingsReadOnlyRow
            label=""
            value={t('NLSBoardSettingsModulesReadOnlyNotice')}
          />
        )}
        {ADDON_CARDS.map((card) => {
          const title = t(card.titleKey)
          const subtitle = t(card.subtitleKey)
          const enabled = isModuleEnabled(workspace, card.moduleKey)
          const isTierLocked =
            card.moduleKey === 'clientOutreach' &&
            !isClientOutreachTierEligible(workspace.plan, workspace)
          const toggleDisabled = !canEdit || isTierLocked
          const tooltip = isTierLocked
            ? t('NLSWorkspaceModulesPaidPlanRequired')
            : undefined
          return renderModuleRow(
            card.key,
            title,
            subtitle,
            enabled,
            toggleDisabled,
            tooltip,
          )
        })}
        {(() => {
          const title = t(FUN_PACK_CARD.titleKey)
          const subtitle = t(FUN_PACK_CARD.subtitleKey)
          const enabled = isModuleEnabled(workspace, FUN_PACK_CARD.moduleKey)
          const toggleDisabled = !canEdit
          return renderModuleRow(
            FUN_PACK_CARD.key,
            title,
            subtitle,
            enabled,
            toggleDisabled,
          )
        })()}
      </SettingsSection>

      {/* Group 2: Helper app */}
      <SettingsSection
        title={t('NLSBoardSettingsSectionHelperApp')}
        description={t('NLSBoardSettingsSectionHelperAppDesc')}
      >
        {HELPER_CARDS.map((card) => {
          const title = t(card.titleKey)
          const subtitle = t(card.subtitleKey)
          const enabled = workspace.modules?.[card.key] !== false
          const isPrereqOff = card.requiredModule
            ? !isModuleEnabled(workspace, card.requiredModule)
            : false
          const toggleDisabled = !canEdit || isPrereqOff
          const tooltip =
            isPrereqOff && card.disabledReasonKey
              ? t(card.disabledReasonKey)
              : undefined
          return renderModuleRow(
            card.key,
            title,
            subtitle,
            enabled,
            toggleDisabled,
            tooltip,
          )
        })}
      </SettingsSection>

      {pendingTurnOff && (
        <ConfirmActionModal
          open={true}
          onClose={() => setPendingTurnOff(null)}
          onConfirm={handleConfirmTurnOff}
          title={t('NLSWorkspaceModulesConfirmTurnOffTitle', {
            module: pendingTurnOff.title,
          })}
          body={t('NLSWorkspaceModulesConfirmTurnOffBody', {
            module: pendingTurnOff.title,
          })}
          confirmLabel={t('NLSWorkspaceModulesConfirmTurnOffConfirm')}
          cancelLabel={t('NLSWorkspaceModulesConfirmTurnOffCancel')}
          danger
        />
      )}
    </div>
  )
}
